import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Spinner,
  Badge,
  Toast
} from 'react-bootstrap';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { firestore, storage } from '../views/firebaseConfig';
import { useAuth } from '../contexts/AuthContext';
import './EditPostModal.css';

export default function EditPostModal({ show, post, onHide, onUpdated }) {
  const { user } = useAuth();
  const userId = user?.uid;

  const [title, setTitle]       = useState(post.postTitle);
  const [text, setText]         = useState(post.postText);
  const [category, setCategory] = useState(post.category);
  const [status, setStatus]     = useState(post.status);

  const [urls, setUrls]         = useState([
    post.image1 || '',
    post.image2 || '',
    post.image3 || ''
  ]);
  const [files, setFiles]       = useState([null, null, null]);

  const [saving, setSaving]     = useState(false);
  const [alert, setAlert]       = useState('');
  const [showAlert, setShowAlert] = useState(false);
  const [showToast, setShowToast] = useState(false);

  const categories = [
    'Pool/Billiards','Food','Job/Gig','Having Fun!','Tournament',
    'League Game','Gaming','Film','Tech','Art','Live Music',
    'Basketball','Football','Soccer','Baseball','Cars','LifeStyle',
    'Travel','Other'
  ];

  useEffect(() => {
    setTitle(post.postTitle);
    setText(post.postText);
    setCategory(post.category);
    setStatus(post.status);
    setUrls([post.image1||'', post.image2||'', post.image3||'']);
    setFiles([null,null,null]);
  }, [post]);

  const minText = 10, minTitle = 5;
  const isDisabled = 
    title.trim().length < minTitle ||
    text.trim().length < minText ||
    (!urls.some(u=>u) && !files.some(f=>f)) ||
    !category;

  const handleFile = (i, f) => {
    const arr = [...files];
    arr[i] = f;
    setFiles(arr);
  };
  const removeSlot = i => {
    if (files[i]) {
      const arr = [...files]; arr[i]=null; setFiles(arr);
    } else {
      const arr = [...urls]; arr[i]=''; setUrls(arr);
    }
  };

  async function uploadNew() {
    const result = [...urls];
    for (let i=0; i<files.length; i++) {
      const f = files[i];
      if (f) {
        const r = ref(storage, `feedMedia/${userId}/${Date.now()}_${f.name}`);
        await uploadBytes(r,f);
        result[i] = await getDownloadURL(r);
      }
    }
    return result;
  }

  const saveChanges = async () => {
    if (isDisabled) return;
    setSaving(true);
    try {
      const finalUrls = await uploadNew();
      let newStatus = status;
      const changed =
        title!==post.postTitle ||
        text !==post.postText ||
        category!==post.category ||
        finalUrls.some((u,i)=>u!==(post[`image${i+1}`]||''));
      if (changed && post.status==='active') newStatus = 'inProcess';

      const updatedPost = {
        feedPostId: post.feedPostId || post.id,
        userId: post.userId || userId,
        nickname: post.nickname || '',
        userImage: post.userImage || '',
        isBusinessPost: post.isBusinessPost || false,
        businessId: post.businessId || null,
        locationId: post.locationId || null,
        postTitle: title.trim(),
        postText:  text.trim(),
        category,
        status:    newStatus,
        image1:    finalUrls[0]||'',
        image2:    finalUrls[1]||'',
        image3:    finalUrls[2]||'',
        timestamp: Timestamp.now()
      };

      await updateDoc(doc(firestore,'feedPost',post.id), updatedPost);
      setShowToast(true);
      onUpdated();
      setTimeout(()=> {
        setShowToast(false);
        onHide();
      }, 1200);
    } catch(err) {
      console.error(err);
      setAlert('Failed to save.');
      setShowAlert(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      scrollable
      backdrop="static"
      keyboard={false}
      dialogClassName="edit-post-dialog"
      contentClassName="edit-post-content"
      backdropClassName="edit-post-backdrop"
    >
      <Modal.Header closeButton>
        <Modal.Title>Edit Pockit</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {showAlert && <div className="alert alert-danger">{alert}</div>}

        <div className="epm-images mb-4">
          {[0, 1, 2].map(i => (
            <div key={i} className="epm-slot">
              {urls[i] ? (
                <div className="epm-thumb-wrap">
                  <img src={urls[i]} alt="" />
                  <button className="epm-remove" onClick={() => removeSlot(i)}>×</button>
                </div>
              ) : files[i] ? (
                <div className="epm-thumb-wrap">
                  <img src={URL.createObjectURL(files[i])} alt="" />
                  <button className="epm-remove" onClick={() => removeSlot(i)}>×</button>
                </div>
              ) : (
                <label className="epm-add">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={e => handleFile(i, e.target.files[0])}
                  />
                  +
                </label>
              )}
            </div>
          ))}
        </div>

        <Form.Group className="mb-3">
          <Form.Label>Title</Form.Label>
          <Form.Control
            value={title}
            onChange={e => setTitle(e.target.value)}
            maxLength={100}
          />
          <Form.Text className={title.trim().length < minTitle ? 'text-danger' : 'text-muted'}>
            {title.trim().length < minTitle
              ? `Minimum ${minTitle} chars`
              : `${100 - title.length} left`}
          </Form.Text>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Description</Form.Label>
          <Form.Control
            as="textarea"
            rows={4}
            value={text}
            onChange={e => setText(e.target.value)}
            maxLength={600}
          />
          <Form.Text className={text.trim().length < minText ? 'text-danger' : 'text-muted'}>
            {text.trim().length < minText
              ? `Minimum ${minText} chars`
              : `${600 - text.length} left`}
          </Form.Text>
        </Form.Group>

        <Row className="mb-3 gx-2">
          <Col>
            <Form.Select value={category} onChange={e => setCategory(e.target.value)}>
              <option value="">Select Category</option>
              {categories.map(c => (
                <option key={c}>{c}</option>
              ))}
            </Form.Select>
          </Col>
          <Col xs="auto">
            <Form.Check
              type="switch"
              label={status === 'active' ? 'Active' : 'Paused'}
              checked={status === 'active'}
              onChange={() => setStatus(s => (s === 'active' ? 'inactive' : 'active'))}
            />
          </Col>
        </Row>

        {post.status === 'active' && (() => {
          const dirty =
            title !== post.postTitle ||
            text !== post.postText ||
            category !== post.category ||
            files.some(f => f) ||
            urls.some((u, i) => u !== (post[`image${i + 1}`] || ''));
          return dirty ? (
            <Badge bg="warning" text="dark" className="mb-3">
              ⚠️ Changes send post back for review
            </Badge>
          ) : null;
        })()}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={saveChanges} disabled={isDisabled || saving}>
          {saving ? <Spinner animation="border" size="sm" /> : 'Save Changes'}
        </Button>
      </Modal.Footer>

      <Toast
        onClose={() => setShowToast(false)}
        show={showToast}
        delay={1500}
        autohide
        className="epm-toast"
      >
        <Toast.Body>✅ Updated!</Toast.Body>
      </Toast>
    </Modal>
  );
}
